import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import { billingApi } from '@/services/billing'
import { useAuthStore } from '@/stores/auth-store'

// How long to keep re-checking after Stripe Checkout returns, while the
// platform webhook marks the org as paid (usually a few seconds).
const PAYMENT_CONFIRM_POLL_MS = 3000
const PAYMENT_CONFIRM_TIMEOUT_MS = 90_000

// Shows trial-days-remaining + an upgrade CTA on the dashboard, confirms a
// just-completed Stripe payment, and bounces to the upgrade wall if the org
// becomes locked mid-session (login.tsx already handles the common case
// right after sign-in).
export function TrialBanner() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const [isRedirecting, setIsRedirecting] = useState(false)

  const billingReturn = searchParams.get('billing')
  const [confirmingSince, setConfirmingSince] = useState<number | null>(() =>
    billingReturn === 'success' ? Date.now() : null,
  )
  const isConfirmingPayment = confirmingSince !== null

  const billingQuery = useQuery({
    queryKey: ['billing-status'],
    queryFn: billingApi.getStatus,
    enabled: isAuthenticated,
    staleTime: isConfirmingPayment ? 0 : 5 * 60 * 1000,
    refetchInterval: isConfirmingPayment ? PAYMENT_CONFIRM_POLL_MS : false,
    retry: false,
  })

  const status = billingQuery.data

  // Clear ?billing=... from the URL once read, so a refresh doesn't replay it.
  useEffect(() => {
    if (!billingReturn) return
    if (billingReturn === 'cancelled') {
      toast.info('Checkout cancelled. You have not been charged.')
    }
    const next = new URLSearchParams(searchParams)
    next.delete('billing')
    setSearchParams(next, { replace: true })
  }, [billingReturn, searchParams, setSearchParams])

  useEffect(() => {
    if (!isConfirmingPayment || !status) return
    if (status.plan === 'active') {
      setConfirmingSince(null)
      toast.success('Payment received — your subscription is active.')
      return
    }
    if (Date.now() - (confirmingSince ?? 0) > PAYMENT_CONFIRM_TIMEOUT_MS) {
      setConfirmingSince(null)
      toast.info('We are still confirming your payment. This page will update shortly — no need to pay again.')
    }
  }, [isConfirmingPayment, confirmingSince, status])

  useEffect(() => {
    // Don't send a just-paid customer to the lock screen while the webhook
    // is still on its way.
    if (status?.is_locked && !isConfirmingPayment) {
      navigate('/billing/locked', { replace: true })
    }
  }, [status?.is_locked, isConfirmingPayment, navigate])

  if (isConfirmingPayment) {
    return (
      <div className="mb-4 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
        <Spinner />
        <p className="text-sm text-foreground">Confirming your payment…</p>
      </div>
    )
  }

  // Only a running trial gets the countdown. Free-plan teams (trial over,
  // within the free user limit) and paid orgs see nothing.
  if (!status || status.plan !== 'trial' || status.is_locked) {
    return null
  }

  const daysRemaining = status.trial_days_remaining ?? 0

  const onUpgrade = async () => {
    setIsRedirecting(true)
    try {
      const { checkout_url } = await billingApi.createCheckoutSession()
      window.location.href = checkout_url
    } catch (error) {
      toast.error(getErrorMessage(error))
      setIsRedirecting(false)
    }
  }

  return (
    <div className="mb-4 flex flex-col items-start justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 sm:flex-row sm:items-center">
      <p className="text-sm text-foreground">
        {daysRemaining === 0
          ? 'Your free trial ends today.'
          : `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left in your free trial.`}
        <span className="text-muted-foreground"> Teams of up to 10 users stay free after the trial.</span>
      </p>
      <Button type="button" size="sm" disabled={isRedirecting} onClick={onUpgrade}>
        Upgrade now
      </Button>
    </div>
  )
}
